import React, { useState } from 'react';
import _isEmpty from 'lodash-es/isEmpty';
import Button from 'components/Buttons/Button';
import DeleteButton from 'components/Buttons/DeleteButton';
import { TrashIcon as Trash } from '@heroicons/react/24/outline';
import 'twin.macro';
import { Tag } from 'types/event';
import { AutoSuggest } from 'components/Form/AutoSuggest';
import { useTranslation } from 'react-i18next';
import { ColorsListDualPalette } from '../Colorpicker/ColorsListDualPalette';
import { ColorPaletteConfig } from 'containers/App/context/useColorPalette';
import { randomBackgroundColor } from 'components/colors';

interface Props {
  onClose: () => void;
  onSubmit: (v: Omit<Tag, 'id'>) => Promise<void>;
  onDelete?: (id: string) => void;
  initialValues?: Omit<Tag, 'id'>;
  tagId?: string;
  options: Array<{ label: string; value: string }>;
  colorPalette: ColorPaletteConfig;
}

export function TagForm(props: Props) {
  const { onClose, tagId, onDelete, options, colorPalette } = props;
  const { t } = useTranslation(['common']);
  const [field, setField] = useState<Omit<Tag, 'id'>>(
    props.initialValues || {
      title: '',
      color: randomBackgroundColor(),
      position: 1,
    },
  );
  const [error, setError] = useState<string | undefined>(undefined);

  return (
    <>
      <div tw="p-1">
        <AutoSuggest
          onChange={(v: string) => {
            if (error) setError(undefined);
            setField({ ...field, title: v });
          }}
          placeholder={t('common:components.tagPicker.tagNamePlaceholder')}
          value={field.title || ''}
          options={options}
          touched={!!error}
          error={error}
          id="tag-name"
          label={`${t('common:components.tagPicker.tagName')}*`}
        />
        <ColorsListDualPalette
          value={field.color || 'white'}
          onChange={c =>
            setField({
              ...field,
              color: c,
            })
          }
          colorPalette={colorPalette}
        />
      </div>
      <div tw="text-right mt-4 flex items-center gap-2 justify-end">
        {tagId && onDelete && (
          <DeleteButton
            aria-label="Supprimer l'étiquette"
            withConfirmation
            onClick={() => onDelete(tagId)}
            minimal
            icon={Trash}
          />
        )}
        <Button minimal onClick={onClose}>
          {t('common:buttons.cancel')}
        </Button>
        <Button
          aria-label={`${tagId ? 'Modifier' : 'Ajouter'} l'étiquette`}
          type="button"
          intent="primary"
          onClick={() => {
            if (_isEmpty((field.title || '').trim())) {
              setError(t('common:messages.fieldCantBeEmpty'));
              return;
            }
            setError(undefined);
            props.onSubmit(field).then(() => onClose());
          }}
        >
          {tagId ? t('common:buttons.modify') : t('common:buttons.add')}
        </Button>
      </div>
    </>
  );
}
